/**
 * RuinSkillEffect — Blades tree sunder evolution: fracturing overhead strike.
 *
 * Replaces Sunder with enhanced parameters and richer branching:
 * - Shield: bypass + apply armor_shred at 40% (Sunder only bypassed)
 * - Armored: armor_shred at 55% for 7s (vs Sunder's 40%/5s)
 * - Unarmored: 1.75× damage (vs Sunder's 1.5×)
 *
 * Implements SkillEffect directly with own cooldown management.
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see blades-spec.md § Ruin
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { DefenseType } from './SunderSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface RuinDeps {
  readonly combat: {
    startAttack(attackType: string): number; // returns enemies hit
  };
  readonly statusEffects: {
    apply(effectId: string, config: {
      sourceSkillId: string;
      durationMs: number;
      intensity: number;
    }): void;
  };
  readonly target: {
    getDefenseType(): DefenseType;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface RuinConfig {
  readonly cooldownMs: number;
  readonly armorShredIntensity: number;
  readonly armorShredDurationMs: number;
  readonly shieldShredIntensity: number;
  readonly unarmoredDamageMultiplier: number;
}

const DEFAULT_CONFIG: RuinConfig = {
  cooldownMs: 1000,                   // medium cooldown tier
  armorShredIntensity: 0.55,          // 55% armor reduction
  armorShredDurationMs: 7000,         // 7s shred duration
  shieldShredIntensity: 0.4,          // 40% armor reduction on shield bypass
  unarmoredDamageMultiplier: 1.75,    // 75% bonus vs unarmored
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class RuinSkillEffect implements SkillEffect {
  readonly skillId = 'sunder_evolved';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: RuinDeps;
  private readonly config: RuinConfig;

  private _cooldownRemaining = 0;
  private _attacksFired = 0;
  private _lastDefenseType: DefenseType | null = null;
  private _shieldBypassed = false;
  private _armorShredApplied = false;
  private _resolvedDamageMultiplier = 1.0;

  constructor(deps: RuinDeps, config: Partial<RuinConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get attacksFired(): number { return this._attacksFired; }
  get lastDefenseType(): DefenseType | null { return this._lastDefenseType; }
  get shieldBypassed(): boolean { return this._shieldBypassed; }
  get armorShredApplied(): boolean { return this._armorShredApplied; }
  get resolvedDamageMultiplier(): number { return this._resolvedDamageMultiplier; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Reset per-activation state
    this._shieldBypassed = false;
    this._armorShredApplied = false;
    this._resolvedDamageMultiplier = 1.0;

    // Check target defense type
    const defenseType = this.deps.target.getDefenseType();
    this._lastDefenseType = defenseType;

    // Fire the fracturing overhead strike
    const enemiesHit = this.deps.combat.startAttack('ruin');
    this._attacksFired++;

    if (enemiesHit > 0) {
      switch (defenseType) {
        case 'shield':
          // Bypass shield AND apply armor_shred (upgrade from Sunder)
          this._shieldBypassed = true;
          this.deps.statusEffects.apply('armor_shred', {
            sourceSkillId: 'ruin',
            durationMs: this.config.armorShredDurationMs,
            intensity: this.config.shieldShredIntensity,
          });
          this._armorShredApplied = true;
          break;

        case 'armored':
          // Apply deeper armor_shred — 55%
          this.deps.statusEffects.apply('armor_shred', {
            sourceSkillId: 'ruin',
            durationMs: this.config.armorShredDurationMs,
            intensity: this.config.armorShredIntensity,
          });
          this._armorShredApplied = true;
          break;

        case 'unarmored':
          // Enhanced bonus damage — 75%
          this._resolvedDamageMultiplier = this.config.unarmoredDamageMultiplier;
          break;
      }
    }

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false; // instant skill
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
